import { captureException, captureMessage } from "@sentry/react";
import { DBSchema, IDBPDatabase, openDB, OpenDBCallbacks } from "idb";

import { AppDispatch } from "@/data/store";
import { PendingSnapshot, setPendingSnapshot } from "@/features/authority";
import { Snapshot } from "@/stores/snapshot";
import { removeItem, setItem } from "@/utils/local-storage";

import type { BrowserStorageNotice } from "./notice";
import { pendingSnapshotParser } from "./utils";

export const IDB_PENDING_STORE_NAME = "pending-snapshots";

export interface PendingDB extends DBSchema {
  [IDB_PENDING_STORE_NAME]: {
    key: [string, string]; // [workId, answerBlockId]
    value: PendingSnapshot & { idbKey: [string, string] };
  };
}

export enum BrowserStorageState {
  NOT_INITIALISED,
  LOCAL_STORAGE,
  INDEXED_DB,
  NONE,
}

/**
 * The IndexedDB schema migration. Each step is guarded on store presence, so a
 * store is never re-created and a database with mismatched version/stores (e.g.
 * after a storage wipe) heals instead of getting stuck.
 *
 * To add a store: bump `idbVersion` and append a presence-guarded
 * `createObjectStore` step, like the one below. Never change an existing step —
 * it has already run on real databases. Only additive changes are supported; a
 * tab open when another upgrades the schema reloads rather than running against
 * it (see {@link BrowserStorage.handleBlocking}).
 *
 * @example
 * if (!db.objectStoreNames.contains("draft-attachments")) {
 *   db.createObjectStore("draft-attachments", { keyPath: "id" });
 * }
 */
export function upgradeBrowserStorage(
  db: IDBPDatabase<PendingDB>,
  _oldVersion: number
) {
  // Guarded on presence so a database missing this store heals here rather than
  // falling back to Local Storage forever.
  if (!db.objectStoreNames.contains(IDB_PENDING_STORE_NAME)) {
    db.createObjectStore(IDB_PENDING_STORE_NAME, { keyPath: "idbKey" });
  }
  // Add later stores here.
}

/**
 * Manages browser storage for answer block snapshots to prevent data loss.
 *
 * Uses IndexedDB if supported, otherwise falls back to Local Storage. Stores
 * snapshots to recover student work if the connection is lost and the tab is
 * closed.
 *
 * All methods are no-op if the browser storage is not available or not
 * initialised.
 */
export class BrowserStorage {
  private state: BrowserStorageState;
  public idb: IDBPDatabase<PendingDB> | undefined;

  /** IndexedDB schema version. To change the schema, bump this and add a step in {@link upgradeBrowserStorage}. */
  private idbVersion: number;

  /** Prefix for this work's keys in browser storage. */
  private workId: string;

  /** Whether the current exam is a LockDown exam. */
  private isLockDown: boolean;

  /** Sink for UI notices (blocked / reload). Injected so this class stays UI-agnostic. */
  private onNotice?: (notice: BrowserStorageNotice) => void;

  constructor({
    isLockDown,
    workId,
    onNotice,
  }: {
    isLockDown: boolean;
    workId: string;
    onNotice?: (notice: BrowserStorageNotice) => void;
  }) {
    this.idbVersion = 1;
    this.workId = workId;
    this.isLockDown = isLockDown;
    this.onNotice = onNotice;
    this.state = BrowserStorageState.NOT_INITIALISED;
  }

  getState() {
    return this.state;
  }

  disable() {
    this.state = BrowserStorageState.NONE;
  }

  /**
   * Opens IndexedDB, falling back to Local Storage (LockDown, or on failure).
   * Never throws — unavailable storage becomes `NONE`. May block until another
   * tab closes (see {@link handleBlocked}), then resumes.
   */
  async initialise() {
    if (!this.isLockDown) {
      try {
        this.idb = await openDB<PendingDB>(
          IDB_PENDING_STORE_NAME,
          this.idbVersion,
          this.idbCallbacks()
        );
      } catch (error) {
        // Opening failed (e.g. IndexedDB disabled) — fall through to the fallback.
        captureException(error, {
          level: "warning",
          tags: { browser_storage: "open_failed" },
          extra: { workId: this.workId },
        });
      } finally {
        // Clear the blocked notice if it was shown (else a no-op "none").
        this.onNotice?.("none");
      }

      if (this.isIDBStoreAvailable() !== true) {
        console.error("Failed to initialise IndexedDB");
      }
    }

    if (this.isIDBStoreAvailable() === true) {
      this.state = BrowserStorageState.INDEXED_DB;
    } else {
      this.state = this.isLocalStorageAvailable()
        ? BrowserStorageState.LOCAL_STORAGE
        : BrowserStorageState.NONE;
    }
  }

  /**
   * Helper method to check if the IndexedDB object store was loaded properly.
   */
  private isIDBStoreAvailable() {
    return (
      this.idb && this.idb.objectStoreNames.contains(IDB_PENDING_STORE_NAME)
    );
  }

  /** `openDB` callbacks — arms the handlers that react when another tab upgrades the schema. */
  private idbCallbacks(): OpenDBCallbacks<PendingDB> {
    return {
      upgrade: (db, oldVersion) => upgradeBrowserStorage(db, oldVersion),
      blocked: (currentVersion, blockedVersion) =>
        this.handleBlocked(currentVersion, blockedVersion),
      blocking: (currentVersion, blockedVersion) =>
        this.handleBlocking(currentVersion, blockedVersion),
      terminated: () =>
        console.warn(
          "IndexedDB connection terminated. Need to reload to enable offline recovery"
        ),
    };
  }

  /**
   * `blocked`: an older tab is holding the DB open, stalling our upgrade. Ask
   * the student to close it; the open resumes and the banner clears when they do.
   */
  private handleBlocked(currentVersion: number, blockedVersion: number | null) {
    console.warn(
      "IndexedDB upgrade blocked by another tab holding an older version open"
    );
    captureMessage("BrowserStorage IndexedDB upgrade blocked", {
      level: "warning",
      tags: { browser_storage: "upgrade_blocked" },
      extra: { currentVersion, blockedVersion, workId: this.workId },
    });
    this.onNotice?.("blocked");
  }

  /**
   * `versionchange`: another tab is upgrading the schema. This tab runs the old
   * bundle, so it doesn't keep going against the new schema — it closes its
   * connection (letting the other tab proceed), stops backing up, and prompts a
   * reload. Reloading picks up the new bundle and re-runs recovery cleanly.
   */
  private handleBlocking(
    currentVersion: number,
    blockedVersion: number | null
  ) {
    this.idb?.close();
    this.idb = undefined;
    this.state = BrowserStorageState.NONE;
    console.warn(
      "IndexedDB superseded by a newer version. Reload to re-enable offline recovery"
    );
    captureMessage("BrowserStorage superseded by a newer version", {
      level: "warning",
      tags: { browser_storage: "version_change" },
      extra: { currentVersion, blockedVersion, workId: this.workId },
    });
    this.onNotice?.("reload");
  }

  // well yes it's a function to test localStorage why not
  private isLocalStorageAvailable() {
    try {
      const storage = window.localStorage;
      storage.setItem("hello", "rishi");
      storage.removeItem("hello");
      return true;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Load stored snapshots from the browser storage for current work.
   *
   * @returns An array of pending snapshots. An empty array if there are no
   * snapshots or the browser storage is not available or throws an error.
   */
  async loadStoredSnapshots(): Promise<PendingSnapshot[]> {
    try {
      let snapshots: PendingSnapshot[] = [];
      switch (this.state) {
        case BrowserStorageState.LOCAL_STORAGE:
          snapshots = this.loadSnapshotsFromLocalStorage();
          break;

        case BrowserStorageState.INDEXED_DB:
          snapshots = await this.loadSnapshotsFromIdb();
          break;

        default:
          break;
      }
      return snapshots;
    } catch (error) {
      // Backup read failed — offline work may be unrecoverable on reload, so
      // surface it rather than returning [] silently.
      captureException(error, {
        level: "warning",
        tags: { browser_storage: "read_failed" },
        extra: { state: this.state, workId: this.workId },
      });
      console.error("Failed to load stored snapshots:", error);
      return [];
    }
  }

  private loadSnapshotsFromLocalStorage() {
    const keys = Object.keys(localStorage);
    const pendingSnapshotsKeys = keys.filter((key) =>
      key.includes(`${this.workId}:pending-snapshot`)
    );
    const pendingSnapshots = pendingSnapshotsKeys.flatMap((key) => {
      const value = localStorage.getItem(key);
      const snapshot = value != null ? pendingSnapshotParser(value) : null;
      return snapshot ?? [];
    });
    return pendingSnapshots;
  }

  private async loadSnapshotsFromIdb() {
    const pendingSnapshots =
      (await this.idb?.getAll(IDB_PENDING_STORE_NAME)) ?? [];
    return pendingSnapshots.filter((snapshot) => {
      if (snapshot?.idbKey == null || !Array.isArray(snapshot?.idbKey))
        return false;
      return snapshot.idbKey[0] === this.workId;
    });
  }

  /**
   * Stores a snapshot in the browser storage if the Browser Storage is
   * available. Otherwise, it's a no-op. Swallows all errors.
   */
  async storeSnapshot(pending: PendingSnapshot) {
    try {
      switch (this.state) {
        case BrowserStorageState.LOCAL_STORAGE:
          setItem(
            `${this.workId}:pending-snapshot`,
            `${pending.answerBlockId}`,
            JSON.stringify(pending)
          );
          break;

        case BrowserStorageState.INDEXED_DB:
          await this.idb?.put(IDB_PENDING_STORE_NAME, {
            ...pending,
            idbKey: [this.workId, pending.answerBlockId],
          });
          break;

        default:
          break;
      }
    } catch (error) {
      // Backup write failed (e.g. quota). If the network save also fails this
      // snapshot is unrecoverable, so surface it rather than only logging.
      captureException(error, {
        level: "warning",
        tags: { browser_storage: "write_failed" },
        extra: {
          state: this.state,
          workId: this.workId,
          answerBlockId: pending.answerBlockId,
          version: pending.snapshot.version,
        },
      });
      console.error(`Failed to store snapshot in the browser storage:`, error);
      return;
    }
  }

  /**
   * Removes snapshots passed as an argument from the browser storage.
   */
  async removeSnapshots(snapshots: PendingSnapshot[]) {
    for (const snapshot of snapshots) {
      this.removeSnapshot(snapshot.answerBlockId);
    }
  }

  /**
   * Removes a snapshot for current work and specific answer block.
   * If the snapshot doesn't exist, nothing happens.
   */
  async removeSnapshot(answerBlockId: string) {
    try {
      switch (this.state) {
        case BrowserStorageState.LOCAL_STORAGE:
          removeItem(`${this.workId}:pending-snapshot`, answerBlockId);
          break;

        case BrowserStorageState.INDEXED_DB:
          await this.idb?.delete(IDB_PENDING_STORE_NAME, [
            this.workId,
            answerBlockId,
          ]);
          break;

        default:
          break;
      }
    } catch (_error) {
      return;
    }
  }

  /**
   * Recovers and updates snapshots from the browser storage, ensuring that only
   * newer snapshots are processed and integrated with the local Redux store.
   *
   * Since it forcefully updates the local snapshots and pending queue,
   * should be called with caution.
   *
   * @param savedSnapshots - The snapshots that were confirmed to be saved
   * @param dispatch - AppDispatch function
   * @returns An array of snapshots that were recovered. Returns an empty array
   * if any error occurs, all errors handled silently inside methods.
   */
  async recoverPendingSnapshots(
    savedSnapshots: Record<string, Snapshot>,
    dispatch: AppDispatch
  ) {
    const storedSnapshots = await this.loadStoredSnapshots();

    const snapshots = storedSnapshots.filter(({ answerBlockId, snapshot }) => {
      const currentSnapshot = savedSnapshots[answerBlockId];
      return !currentSnapshot || currentSnapshot.version < snapshot.version;
    });

    snapshots.forEach(({ answerBlockId, snapshot }) => {
      dispatch(
        setPendingSnapshot({
          answerBlockId,
          snapshot,
          updateLocalSnapshot: true,
        })
      );
    });

    this.removeSnapshots(storedSnapshots);

    return snapshots;
  }
}
